import { lazy, Suspense, useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ToastProvider } from "@/contexts/ToastContext";
import { bindGlobalClickSfx } from "@/lib/sfx";
import { bindGlobalRipple } from "@/lib/ripple";
import { warmUp as warmP2P } from "@/lib/p2p";
import { ContextMenuProvider } from "@/contexts/ContextMenuContext";
import { LibraryProvider } from "@/contexts/LibraryContext";
import { ListenTogetherProvider } from "@/contexts/ListenTogetherContext";
import { PageSearchProvider } from "@/contexts/PageSearchContext";
import { ConnectProvider } from "@/contexts/ConnectContext";
import { PlayerProvider } from "@/contexts/PlayerContext";
import { MainLayout } from "@/layouts/MainLayout";
import { ProtectedRoute } from "@/routes/ProtectedRoute";
import { PublicOnlyRoute } from "@/routes/PublicOnlyRoute";

import { LoginPage } from "@/pages/Login/LoginPage";
import { RegisterPage } from "@/pages/Register/RegisterPage";
import { VerifyPage } from "@/pages/Verify/VerifyPage";
import { ResetPage } from "@/pages/Reset/ResetPage";
import { HomePage } from "@/pages/Home/HomePage";
import { AlbumPage } from "@/pages/Album/AlbumPage";
import { ArtistPage } from "@/pages/Artist/ArtistPage";
import { ArtistLikedPage } from "@/pages/Artist/ArtistLikedPage";
import { PlaylistPage } from "@/pages/Playlist/PlaylistPage";
import { MixPage } from "@/pages/Mix/MixPage";
import { OfflinePage } from "@/pages/Offline/OfflinePage";
import { SearchPage } from "@/pages/Search/SearchPage";
import { LibraryPage } from "@/pages/Library/LibraryPage";
import { LikedPage } from "@/pages/Liked/LikedPage";
import { RadioPage } from "@/pages/Radio/RadioPage";
import { SinglePage } from "@/pages/Single/SinglePage";
import { GenrePage } from "@/pages/Genre/GenrePage";
import { UserProfilePage } from "@/pages/User/UserProfilePage";
import { NotFoundPage } from "@/pages/NotFound/NotFoundPage";
import { AccountPage } from "@/pages/Account/AccountPage";
import { PrivacyPage } from "@/pages/Legal/PrivacyPage";
import { TermsPage } from "@/pages/Legal/TermsPage";

// heavier / low-frequency pages — split so their code doesn't sit in the
// main chunk that every single page load has to parse and run.
const StatsPage = lazy(() => import("@/pages/Stats/StatsPage").then((m) => ({ default: m.StatsPage })));
const SettingsPage = lazy(() => import("@/pages/Settings/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const ImportPage = lazy(() => import("@/pages/Import/ImportPage").then((m) => ({ default: m.ImportPage })));
const WrappedPage = lazy(() => import("@/pages/Wrapped/WrappedPage").then((m) => ({ default: m.WrappedPage })));

export default function App() {
  useEffect(() => { bindGlobalClickSfx(); bindGlobalRipple(); warmP2P(); }, []);
  return (
    <AuthProvider>
      <ToastProvider>
      <LibraryProvider>
        <PlayerProvider>
          <ConnectProvider>
          <ListenTogetherProvider>
            <ContextMenuProvider>
              <PageSearchProvider>
                <Routes>
                  <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
                  <Route path="/register" element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>} />
                  <Route path="/verify" element={<VerifyPage />} />
                  <Route path="/reset" element={<ResetPage />} />
                  <Route path="/*" element={<ProtectedRoute><MainLayout><Suspense fallback={null}><Inner /></Suspense></MainLayout></ProtectedRoute>} />
                </Routes>
              </PageSearchProvider>
            </ContextMenuProvider>
          </ListenTogetherProvider>
          </ConnectProvider>
        </PlayerProvider>
      </LibraryProvider>
      </ToastProvider>
    </AuthProvider>
  );
}

function Inner() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/library" element={<LibraryPage />} />
      <Route path="/liked" element={<LikedPage />} />
      <Route path="/album/:id" element={<AlbumPage />} />
      <Route path="/artist/:id" element={<ArtistPage />} />
      <Route path="/artist/:id/liked" element={<ArtistLikedPage />} />
      <Route path="/playlist/:id" element={<PlaylistPage />} />
      <Route path="/mix/:id" element={<MixPage />} />
      <Route path="/offline" element={<OfflinePage />} />
      <Route path="/track/:id" element={<SinglePage />} />
      <Route path="/genre/:slug" element={<GenrePage />} />
      <Route path="/user/:username" element={<UserProfilePage />} />
      <Route path="/radio" element={<RadioPage />} />
      <Route path="/stats" element={<StatsPage />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/import" element={<ImportPage />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/wrapped" element={<WrappedPage />} />
      <Route path="/wrapped/:period" element={<WrappedPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
